import React, { useCallback, useState } from "react";
import { Box, CircularProgress, Alert, Typography } from "@mui/material";
import { apiClient } from "../utils/http";
import { useAuth } from "../context/AuthContext";
import usePaginatedList from "../hooks/usePaginatedList";
import useDebouncedValue from "../hooks/useDebouncedValue";
import OrderCard from "../components/OrderCard";
import OrdersAppBar from "../components/OrdersAppBar";
import LoadMoreButton from "../components/LoadMoreButton";

export default function OnlineOrders() {
  const { store } = useAuth();
  const currency = store?.currency || store?.price_type || "";

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);

  // GET pos/v1/orders/get-orders/1?st=placed&search=...&page=N — the trailing
  // route param is `online` (OrderController@getOrders($request, $online=0)) —
  // 1 filters to is_online. st=placed narrows to online orders staff haven't
  // acted on yet (same 'placed' status POSController@initPOS's own
  // online-orders query uses) — once accepted, an order moves to Live Orders
  // (processing/modifying) or Completed Orders (completed/cancelled), so this
  // tab is deliberately just the unprocessed queue, not every online order ever.
  const fetchPage = useCallback(
    (page) =>
      apiClient.get("orders/get-orders/1", { params: { st: "placed", search: debouncedSearch, page } }).then((res) => {
        const paginator = res.data?.orders || {};
        return { items: paginator.data || [], hasMore: (paginator.current_page || 1) < (paginator.last_page || 1) };
      }),
    [debouncedSearch]
  );
  const { items: orders, loading, loadingMore, hasMore, error, loadMore } = usePaginatedList(fetchPage, [debouncedSearch]);

  return (
    <Box>
      <OrdersAppBar title="Online Orders" search={search} onSearchChange={setSearch} />

      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
            {debouncedSearch ? "No matching orders." : "No unprocessed online orders right now."}
          </Typography>
        ) : (
          <>
            {orders.map((o) => (
              <OrderCard key={o.id} order={o} currency={currency} />
            ))}
            <LoadMoreButton hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} />
          </>
        )}
      </Box>
    </Box>
  );
}
